import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  TrendingDown,
  Share2,
  Compass,
  FileText,
  Search,
  Phone,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
  PlayCircle,
  Mail,
  MessageSquare,
  Heart,
  ShoppingBag,
  BarChart3,
  Briefcase,
  Crown,
  Star,
  Store,
  RefreshCw,
  MapPin,
  Plus,
  Minus,
  Brain,
  Coins,
  ArrowRight,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import ClientLogosWall from '../../components/sections/ClientLogosWall';
import HubSpotModal from '../../components/ui/hubspot-modal';
import { BreadcrumbSchema, FAQPageSchema } from '../../lib/utils/schema-utils';

const PAGE_URL = 'https://recruteurs.io/cabinet-recrutement-marseille/';

// — Typography aligned on home / campagnes / recrutement / externalisation —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// ─── DATA ─────────────────────────────────────────────────────────────

// FAQ enrichie : 7 questions historiques + 4 nouvelles stratégiques SEO
const faqItems = [
  {
    question: 'Quels types de postes recrutez-vous à Marseille ?',
    answer:
      'Nous recrutons principalement des profils commerciaux (terrain, KAM, directeurs régionaux), des cadres et managers, des fonctions support (RH, finance, administration) ainsi que des profils retail et distribution spécialisée. Nous intervenons sur des CDI, des contrats de management de transition et des missions de direction.',
  },
  {
    question: 'Quelle est la différence entre Recruteurs.io et un cabinet de recrutement classique ?',
    answer:
      "La différence principale réside dans notre Plateforme Parcours Découverte. Avant le premier entretien, chaque candidat traverse un environnement digital sur-mesure qui lui montre la vraie réalité de votre entreprise — culture, équipe, mission, grille de rémunération. Le candidat décide en connaissance de cause. Vous éliminez les no-show, vous divisez la casse des 90 premiers jours, vous fidélisez à 12 mois.",
  },
  {
    question: 'Intervenez-vous uniquement à Marseille ?',
    answer:
      "Non. Nous couvrons la région marseillaise au sens large : Aix-en-Provence, Aubagne, Cassis, La Ciotat, Vitrolles, Marignane, Salon-de-Provence, et plus généralement toute la PACA selon la nature de la mission. Pour des postes de direction ou des profils rares, nous recrutons à l'échelle nationale.",
  },
  {
    question: 'Comment se déroule une mission de recrutement avec Recruteurs.io ?',
    answer:
      "La mission commence par un brief approfondi pour comprendre l'environnement, les attendus réels du poste et la culture d'équipe. Nous construisons ensuite un ciblage précis, activons notre réseau et nos outils de sourcing IA, qualifions les profils en entretien structuré, leur faisons traverser votre Plateforme Parcours Découverte et vous présentons un dossier complet — pas une liste de CV. Nous accompagnons ensuite l'intégration sur 90 jours.",
  },
  {
    question: 'Quelle est votre approche sur le recrutement de commerciaux à Marseille ?',
    answer:
      "Les commerciaux sont notre cœur de métier sur Marseille. Nous évaluons la motivation réelle, la solidité du projet professionnel et la compatibilité avec votre secteur et votre culture commerciale — pas seulement le palmarès chiffré. Un bon commercial dans une autre entreprise n'est pas automatiquement le bon commercial chez vous. Nous connaissons les spécificités du Sud-Est : densité concurrentielle, mobilité géographique, attentes salariales et culturelles.",
  },
  {
    question: 'Proposez-vous une garantie sur les recrutements ?',
    answer:
      "Oui, une garantie chiffrée et opposable : replacement gratuit en cas de rupture dans les 6 mois suivant l'embauche, diagnostic post-mortem honnête (où ça a vraiment lâché), et 30 % du fee restitué si vous ne souhaitez pas de replacement. Nous ne promettons pas l'impossible, mais nous nous engageons à ce que la garantie soit réelle, pas un argument commercial.",
  },
  {
    question: 'Combien coûte un cabinet de recrutement à Marseille ?',
    answer:
      "En moyenne sur le marché : 15 % à 30 % du salaire annuel brut chargé, soit 7 500 € à 15 000 € pour un poste à 50 k€. Pour les profils rares ou de direction, le fee peut dépasser 20 000 €. recruteurs.io propose deux modèles : avec retainer (engagement initial qui inclut la production du Parcours Découverte) ou au succès uniquement (fee plus élevé, production Parcours facturée séparément). Tarifs présentés en transparence dès le premier appel découverte.",
  },
  {
    question: 'Cabinet de recrutement Marseille IT : quelles spécificités ?',
    answer:
      "Le marché IT marseillais est tendu : développeurs, ingénieurs DevOps, data engineers, profils cybersécurité — tous sont sur-sollicités sur LinkedIn. Notre approche combine chasse de tête senior et IA appliquée au sourcing pour atteindre les profils passifs qui ne candidatent pas. La Plateforme Parcours Découverte fait la différence : sur des profils tech rares, montrer la stack, l'équipe et le quotidien du poste avant l'entretien augmente nettement les taux de conversion candidat.",
  },
  {
    question: 'Cabinet de recrutement Marseille pour PME : par où commencer ?',
    answer:
      "Le point de départ est toujours un appel de cadrage gratuit de 30 minutes. Nous identifions la nature du besoin (poste critique à l'unité ? volume récurrent ? campagne marque employeur ?), les contraintes (timing, budget, secteur), et nous vous orientons vers la bonne formule. Pour les PME marseillaises 30-150 collaborateurs, c'est généralement le format \"recrutement à l'unité avec Plateforme Parcours Découverte\" qui sécurise le mieux le résultat sans engager une ligne budgétaire massive.",
  },
  {
    question: 'Quelle différence entre un cabinet de recrutement et un chasseur de tête à Marseille ?',
    answer:
      "Un cabinet classique reçoit des candidatures en réponse à une annonce publique. Un chasseur de tête approche directement des profils en poste, identifiés par recherche active, sans annonce. recruteurs.io combine les deux : annonces structurées sur les jobboards quand pertinent, ET chasse de tête senior sur les profils passifs, le tout amplifié par l'IA pour décupler le sourcing. Vous obtenez l'efficacité de la chasse avec la richesse du flux candidatures classiques.",
  },
  {
    question: 'Recruteurs.io intervient-il sur les postes de direction à Marseille ?',
    answer:
      "Oui, nous menons des missions confidentielles de chasse de tête sur des postes de DG, DAF, DRH, directeurs commerciaux et industriels — y compris en contexte sensible (restructuration, transformation, succession dirigeant). Notre approche directe et notre Plateforme Parcours Découverte permettent au dirigeant ciblé de comprendre votre projet avant tout engagement, ce qui sécurise nettement la prise de poste sur ces missions à fort enjeu.",
  },
];

// — Section 2 — Marque employeur en jeu (3 cartes Talent Board) ————————
const brandRiskStats = [
  {
    icon: Share2,
    metric: '72 %',
    title: 'partagent leur expérience candidat',
    description:
      "Chaque candidat de votre process en parle. À ses pairs, à ses collègues, sur LinkedIn, sur Glassdoor. Que vous l'embauchiez ou non. Source : Talent Board.",
  },
  {
    icon: TrendingDown,
    metric: '64 %',
    title: 'arrêtent d’acheter chez la marque',
    description:
      "Les candidats déçus ne vous oublient pas — ils boycottent. Pour une marque B2C marseillaise (retail, restauration, services, immobilier), le coût peut atteindre plusieurs centaines de milliers d'euros par an. Source : Talent Board.",
  },
  {
    icon: AlertTriangle,
    metric: '81 %',
    title: 'blacklistent une marque qui les ghoste',
    description:
      "Une candidature sans réponse, un process qui s'éteint en silence : c'est un client, un futur candidat et son réseau qui partent définitivement. Source : étude marché 2024.",
  },
];

// — Section 3 — Méthode 7 temps ————————————————————————————————————————
const methodSteps = [
  {
    icon: FileText,
    title: 'Cadrage business du besoin',
    description:
      "Pas une fiche de poste générique : un diagnostic d'enjeu. Pourquoi ce poste, pour quel résultat, dans quel contexte managérial, avec quels critères de réussite mesurables.",
  },
  {
    icon: Compass,
    title: 'Réécriture engageante du brief candidat',
    description:
      "Nous transformons votre fiche de poste en mission claire, honnête, attirante. Le candidat doit comprendre POURQUOI ce poste vaut son temps avant de comprendre quoi y faire.",
  },
  {
    icon: Search,
    title: 'Sourcing chasse de tête + IA',
    description:
      "Approche directe sur les profils passifs marseillais et PACA, scoring sémantique IA pour élargir intelligemment, qualification fine. La chasse senior pilote, l'IA accélère.",
  },
  {
    icon: Phone,
    title: 'Pré-qualification soignée',
    description:
      "Chaque candidat reçoit un appel de pré-qualification respectueux et structuré, en 30 minutes. C'est souvent leur premier contact avec votre entreprise — nous le traitons comme tel.",
  },
  {
    icon: Sparkles,
    title: 'Plateforme Parcours Découverte',
    description:
      "Les candidats short-listés traversent votre Parcours Découverte. Ils découvrent votre culture, votre équipe, votre quotidien réel, votre rémunération. Ils décident en connaissance de cause.",
  },
  {
    icon: CheckCircle2,
    title: 'Signature et ré-engagement',
    description:
      "Le candidat retenu se ré-engage explicitement à la sortie du Parcours. Plus de signature à l'aveugle, plus de no-show au jour J, plus de rétractation au dernier moment.",
  },
  {
    icon: ShieldCheck,
    title: 'Intégration et suivi 90 jours',
    description:
      "Onboarding personnalisé dès la signature, points de feedback RH et manager, objectifs suivis semaine par semaine. Le recrutement n'est solide qu'au 90e jour — pas à la signature.",
  },
];

// — Section 4 — 4 engagements expérience candidat ————————————————————
const candidateCommitments = [
  {
    icon: Mail,
    title: 'Réponse écrite à 100 % des candidatures',
    description:
      "Sous 48 heures ouvrées. Aucun ghosting, jamais. Même un refus est une réponse soignée — c'est ce qui fait la différence dans la mémoire des candidats.",
  },
  {
    icon: MessageSquare,
    title: 'Brief honnête sur le poste, l’équipe, le contexte',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Le candidat reçoit la vraie réalité — y compris les contraintes — pour pouvoir décider en adulte.",
  },
  {
    icon: RefreshCw,
    title: 'Debrief structuré à chaque étape',
    description:
      "Le candidat sait pourquoi il avance, ou pourquoi il s'arrête. Avec des éléments concrets. Pour qu'il progresse, même s'il n'est pas retenu chez vous.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de surprise après la signature, plus de regret six mois après. Le candidat voit la réalité avant de s'engager — et n'avance que s'il est aligné.",
  },
];

// — Section 5 — Spécialités locales (réutilisé + raffiné) ——————————————
const specialites = [
  {
    icon: Briefcase,
    title: 'Recrutement commercial à Marseille',
    description:
      "Commerciaux terrain, KAM, business developers, directeurs régionaux, directeurs commerciaux. Nous évaluons la motivation, le projet professionnel et la compatibilité culturelle — pas seulement les objectifs atteints. Le Sud-Est est un terrain exigeant : nous connaissons ses spécificités sectorielles.",
  },
  {
    icon: Crown,
    title: 'Recrutement cadres et managers',
    description:
      "Responsables de service, directeurs opérationnels, managers d'équipe, cadres dirigeants. Nous menons des approches directes et confidentielles sur des profils qui ne candidatent pas spontanément. La discrétion est un prérequis, pas une option.",
  },
  {
    icon: Store,
    title: 'Retail et distribution spécialisée',
    description:
      "Directeurs de magasin, responsables de rayon, chefs de secteur, directeurs régionaux retail. Nous comprenons les rythmes et les contraintes du commerce physique en région PACA, où la densité de points de vente et la concurrence RH sont fortes.",
  },
  {
    icon: Star,
    title: 'Fonctions support',
    description:
      "RH, finance, contrôle de gestion, administration des ventes, juridique, office management. Ces profils sont souvent sous-estimés dans leur impact sur la performance opérationnelle. Nous leur accordons la même rigueur de qualification.",
  },
];

// — Section 6 — Double dividende ————————————————————————————————————
const dividendStats = [
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits ou services — y compris s'ils n'ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: ShoppingBag,
    metric: '× 4,4 M £',
    text:
      "C'est ce que Virgin Media a chiffré comme revenu perdu chaque année à cause de ses candidats refusés qui résiliaient leur abonnement.",
  },
  {
    icon: BarChart3,
    metric: '91 % vs 26 %',
    text:
      "des employeurs admettent l'impact de l'expérience candidat sur leurs ventes — 26 % seulement la mesurent. Le gap est votre opportunité.",
  },
];

// — Section 7 — Comparatif (data conservée + 1 ligne ajoutée) ——————————
const comparatif = [
  {
    critere: 'Plateforme Parcours Découverte',
    recruteurs: 'Environnement digital sur-mesure pour chaque candidat',
    classique: 'Aucun équivalent',
  },
  {
    critere: 'Expérience candidat',
    recruteurs: "Parcours immersif avant l'entretien",
    classique: 'Transmission de CV + entretien standard',
  },
  {
    critere: 'Sourcing',
    recruteurs: 'Approche directe + réseau + outils IA',
    classique: 'Jobboards et CVthèque',
  },
  {
    critere: 'Qualification',
    recruteurs: 'Entretiens structurés + évaluation motivationnelle',
    classique: 'Entretien de présélection rapide',
  },
  {
    critere: 'Onboarding candidat',
    recruteurs: 'Intégré dans la mission, suivi post-prise de poste',
    classique: 'Rarement inclus',
  },
  {
    critere: 'Transparence tarifaire',
    recruteurs: 'Formules claires, présentées dès le premier appel',
    classique: 'Variable, souvent négocié après brief',
  },
  {
    critere: 'Suivi après embauche',
    recruteurs: 'Inclus — points à J+30 et J+90',
    classique: 'Rare ou facturable en option',
  },
  {
    critere: 'Garantie',
    recruteurs: 'Replacement < 6 mois + 30 % fee restitué si pas de replacement',
    classique: 'Variable, conditions souvent floues',
  },
];

// — Section 9 — Garantie ————————————————————————————————————————————
const guaranteeBullets = [
  {
    icon: RefreshCw,
    title: 'Replacement gratuit en cas de rupture < 6 mois',
    description:
      "Si votre nouvelle recrue rompt son contrat dans les 6 premiers mois, nous relançons un sourcing complet sans frais supplémentaires.",
  },
  {
    icon: FileText,
    title: 'Diagnostic post-mortem honnête',
    description:
      "Nous identifions où ça a vraiment lâché : projection candidat, package, management, contexte business. Pour ne pas refaire la même erreur.",
  },
  {
    icon: ShieldCheck,
    title: '30 % du fee restitué',
    description:
      "Si vous ne souhaitez pas de replacement, nous vous restituons 30 % du fee. Pour que la garantie soit réelle — pas un argument commercial.",
  },
];

// — Section 10 — Zones couvertes ————————————————————————————————————
const zones = [
  'Marseille (tous arrondissements)',
  "Aix-en-Provence et le Pays d'Aix",
  'Aubagne',
  'Cassis',
  'La Ciotat',
  'Vitrolles',
  'Marignane',
  'Salon-de-Provence',
  'Toulon et PACA selon mission',
];

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  name: 'Recruteurs.io',
  description:
    'Cabinet de recrutement spécialisé en région marseillaise. Commerciaux, cadres, retail et fonctions support. Plateforme Parcours Découverte propriétaire.',
  url: 'https://recruteurs.io',
  email: 'contact@recruteurs.io',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '11 chemin du Mont Gibaou',
    addressLocality: 'Cassis',
    postalCode: '13260',
    addressCountry: 'FR',
  },
  areaServed: [
    { '@type': 'City', name: 'Marseille' },
    { '@type': 'City', name: 'Aix-en-Provence' },
    { '@type': 'City', name: 'Aubagne' },
    { '@type': 'City', name: 'Cassis' },
    { '@type': 'City', name: 'La Ciotat' },
    { '@type': 'City', name: 'Vitrolles' },
    { '@type': 'City', name: 'Marignane' },
    { '@type': 'City', name: 'Salon-de-Provence' },
    { '@type': 'AdministrativeArea', name: 'PACA' },
  ],
  priceRange: '€€€',
  logo: 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png',
  sameAs: [
    'https://www.linkedin.com/company/recruteurs-io',
    'https://twitter.com/recruteurs_io',
    'https://www.facebook.com/recruteurs.io',
  ],
};

// ─── COMPONENT ───────────────────────────────────────────────────────

export default function CabinetRecrutementMarseillePilierPage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Layout>
      <Helmet>
        <title>Cabinet de recrutement à Marseille | Recruteurs.io</title>
        <meta
          name="description"
          content="Cabinet de recrutement à Marseille, Aix et PACA : cadres, commerciaux, dirigeants. Chasse de tête + IA, garantie 6 mois opposable, tarifs publics."
        />
        <meta
          name="keywords"
          content="cabinet de recrutement innovant marseille, recrutement innovant marseille, cabinet recrutement marseille, chasse de tête marseille, recrutement cadre marseille, recrutement commercial marseille, IA recrutement, parcours découverte"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Cabinet de recrutement à Marseille', url: PAGE_URL },
        ]}
      />

      <FAQPageSchema questions={faqItems} />

      {/* HERO — layout centré + image skyline Marseille en fond transparent */}
      <SectionBackground
        page="local-marseille"
        section="lm-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef4e6] to-[#e0f2ff]"
      >
        {/* Image skyline Marseille en background, opacité réduite */}
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none select-none flex items-center justify-center"
        >
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/3.png"
            alt=""
            className="w-full h-full object-cover opacity-15 md:opacity-20"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
          {/* Voile blanc supplémentaire pour garantir la lisibilité */}
          <div className="absolute inset-0 bg-white/30" />
        </div>

        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Cabinet de recrutement innovant · Marseille · Aix · PACA
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Cabinet de recrutement </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  innovant à Marseille
                </span>
              </h1>

              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Le seul <strong>cabinet de recrutement innovant à Marseille</strong> qui
                combine méthode senior, <strong>Plateforme Parcours Découverte</strong> propriétaire
                (onboarding du candidat <em>avant</em> signature), IA appliquée au sourcing et
                garantie 6 mois opposable. Commerciaux, cadres, dirigeants, retail, fonctions support —
                Marseille, Aix-en-Provence et toute la PACA.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/reservation')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Réserver un échange (15 min)
                </button>
                <button
                  onClick={() => navigate('/decouvrir')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  Voir le Parcours Découverte
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 1 bis — Les 4 innovations qui nous distinguent à Marseille */}
      <SectionBackground page="local-marseille" section="lm-innov" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce qui nous rend différents</span>
            <h2 className={h2Class}>
              Pourquoi nous sommes le{' '}
              <span className={gradientText}>
                cabinet de recrutement le plus innovant à Marseille.
              </span>
            </h2>
            <p className="text-lg md:text-xl text-neutral-600 leading-relaxed">
              «&nbsp;Innovant&nbsp;» ne veut rien dire sans preuve. Voici les
              quatre innovations contractuelles et mesurables qui distinguent
              recruteurs.io des autres cabinets de recrutement à Marseille.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Sparkles,
                title: 'Plateforme Parcours Découverte propriétaire',
                body:
                  "Onboarding du candidat avant signature. Vidéos équipe, journée type, grille de rémunération transparente. Le candidat décide en connaissance de cause — résultat moyen observé : −50 % de no-show, −40 % de turnover 90 jours. Aucun autre cabinet à Marseille n'a cet asset.",
                tag: 'Asset tech unique en PACA',
              },
              {
                icon: Brain,
                title: 'IA appliquée au recrutement depuis 2022',
                body:
                  'Trois outils maison en production : Mapping RH (compatibilité candidat ↔ équipe via GPT-4), scoring sémantique de CV sur vos référentiels métier, IA générative pour vos contenus marque employeur. Pas un slide marketing — des outils utilisés sur nos missions tous les jours.',
                tag: 'Précurseur IA RH en PACA',
              },
              {
                icon: ShieldCheck,
                title: 'Garantie 6 mois opposable',
                body:
                  "Garantie écrite, conditions précises : replacement gratuit en cas de rupture < 6 mois, OU 30 % du fee restitué si vous refusez le replacement, OU diagnostic post-mortem honnête fourni dans tous les cas. Standard marché : 3 mois flou. Nous doublons la durée et clarifions par écrit.",
                tag: 'Contractuel, pas marketing',
              },
              {
                icon: Coins,
                title: 'Tarifs publics, chiffrage dès le premier appel',
                body:
                  "Nos tarifs sont sur le site. Pas de négociation cachée, pas de devis flou. Au premier rendez-vous, vous repartez avec un chiffrage précis pour votre poste, et un diagnostic d'enjeu — même si vous décidez de ne pas signer avec nous.",
                tag: 'Transparence totale',
              },
            ].map((it, idx) => {
              const Icon = it.icon;
              return (
                <div
                  key={idx}
                  className="rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all p-7"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md shadow-[#d03484]/20">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-[#d03484] bg-[#fde7f2]/60 rounded-full px-2.5 py-0.5 mb-2">
                        {it.tag}
                      </span>
                      <h3 className="text-lg font-bold text-[#30475e] leading-snug">
                        {it.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 leading-relaxed">
                    {it.body}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Punchline + CTA */}
          <div className="max-w-3xl mx-auto text-center mt-12">
            <p className="text-sm text-neutral-500 mb-4">
              Quatre innovations. Toutes <strong>vérifiables</strong>. Toutes{' '}
              <strong>contractuelles</strong>. Pas de marketing creux.
            </p>
            <button
              onClick={() => navigate('/pourquoi-recruteurs-io')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Voir le comparatif complet vs cabinet classique
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 2 — Marque employeur en jeu */}
      <SectionBackground page="local-marseille" section="lm-stake" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le vrai coût d’un recrutement mal traité</span>

            <h2 className={h2Class}>
              Pour vous, c’est un poste à pourvoir.{' '}
              <span className={gradientText}>
                Pour 80 candidats marseillais, c’est leur seul contact avec votre marque.
              </span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sur un poste cadre à Marseille, vous allez recevoir entre 50 et 100 candidatures.
              Vous en embaucherez une. Les autres ne disparaissent pas dans le silence —
              ils retournent à leur réseau, à leurs collègues, à LinkedIn, à Glassdoor.
              Et la région marseillaise est une boucle où tout le monde se connaît.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {brandRiskStats.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {item.metric}
                    </p>
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Ancrage local */}
      <SectionBackground page="local-marseille" section="lm-anchor" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre ancrage régional</span>

            <h2 className={h2Class}>
              Un cabinet de recrutement{' '}
              <span className={gradientText}>ancré en région marseillaise.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Recruteurs.io intervient dans la région marseillaise et ses bassins d’emploi
              connexes. Nous connaissons le tissu économique local, ses secteurs porteurs,
              ses tensions de recrutement, ses spécificités culturelles et les attentes
              concrètes des dirigeants de PME et d’ETI.
            </p>
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 items-start">
              <div className="space-y-4">
                <p className={bodyClass}>
                  Nous ne sommes pas un annuaire de CV. Nous menons des missions de recrutement
                  avec une vraie compréhension du terrain, de la cible et du contexte d’entreprise.
                  Cela change le résultat : les candidats que nous vous présentons ont déjà
                  compris votre environnement avant le premier entretien.
                </p>
                <p className={bodyClass}>
                  Notre ancrage régional se traduit aussi par un réseau de candidats actifs et
                  passifs sur les bassins d’emploi marseillais — des profils souvent
                  inaccessibles via les jobboards traditionnels.
                </p>
              </div>
              <div className="space-y-4">
                <p className={bodyClass}>
                  Adresse opérationnelle : <strong>11 chemin du Mont Gibaou, 13260 Cassis</strong>.
                  Nous nous déplaçons partout sur Marseille intra-muros, le Pays d’Aix,
                  les Bouches-du-Rhône et la PACA selon la nature de la mission.
                </p>
                <p className={bodyClass}>
                  Pour des postes de direction ou des profils rares, nous recrutons à l’échelle
                  nationale tout en gardant une connaissance fine des contextes locaux.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Méthode 7 temps */}
      <SectionBackground page="local-marseille" section="lm-method" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode en 7 temps</span>

            <h2 className={h2Class}>
              Recruter mieux, pas juste plus vite.{' '}
              <span className={gradientText}>Exigeant côté business, premium côté candidat.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Un recrutement efficace ne se mesure pas au délai de présentation, mais à la
              durée dans le poste. Notre process est conçu en conséquence — du diagnostic
              d’enjeu jusqu’à l’ancrage de la prise de poste au 90&shy;e jour.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Engagements expérience candidat */}
      <SectionBackground page="local-marseille" section="lm-cx" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vivent vos candidats</span>

            <h2 className={h2Class}>
              Quatre promesses concrètes.{' '}
              <span className={gradientText}>Opposables. Vérifiables.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {candidateCommitments.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Spécialités locales */}
      <SectionBackground page="local-marseille" section="lm-specialites" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos spécialités à Marseille</span>

            <h2 className={h2Class}>
              Les fonctions où la qualité du recrutement{' '}
              <span className={gradientText}>change la performance.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specialites.map((s, index) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{s.title}</h3>
                  <p className={bodyClass}>{s.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a
              href="/entreprise/recrutement"
              className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
            >
              Voir notre méthode complète →
            </a>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Double dividende */}
      <SectionBackground page="local-marseille" section="lm-dividend" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>

            <h2 className={h2Class}>
              Vous embauchez 1 candidat.{' '}
              <span className={gradientText}>Vous gagnez 79 ambassadeurs marseillais.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sur un poste, 80 candidatures, 1 embauche. Les 79 autres ne disparaissent
              pas — ils parlent. À leurs collègues, sur LinkedIn, à leur prochain
              entretien. Dans une région où le bouche-à-oreille pro est aussi resserré
              que celui de Marseille, ce qu’ils diront de vous compte double.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {stat.metric}
                    </p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              recruteurs.io vous garantit que ce sera une bonne histoire — celle que
              <strong> vous</strong> avez choisi de raconter. Pas une rumeur de couloir,
              pas un avis Glassdoor frustré.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — Comparatif */}
      <SectionBackground page="local-marseille" section="lm-vs" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Recruteurs.io vs cabinet classique</span>

            <h2 className={h2Class}>
              Ce qui différencie notre approche n’est pas le discours.{' '}
              <span className={gradientText}>C’est ce que nous faisons à chaque étape.</span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto rounded-[2rem] ring-1 ring-neutral-200 overflow-hidden bg-white">
            <div className="hidden md:grid grid-cols-[1fr_1fr_1fr] bg-neutral-900 text-white">
              <div className="p-5 text-xs font-semibold uppercase tracking-widest">
                Critère
              </div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest bg-gradient-to-r from-[#d03484] to-[#d47254]">
                recruteurs.io
              </div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest text-neutral-400">
                Cabinet classique
              </div>
            </div>

            {comparatif.map((row, idx) => (
              <div
                key={row.critere}
                className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] ${
                  idx !== comparatif.length - 1 ? 'border-b border-neutral-100' : ''
                } ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfaf8]'}`}
              >
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider md:hidden mb-1">
                    Critère
                  </p>
                  <p className="text-base font-semibold text-[#30475e]">{row.critere}</p>
                </div>
                <div className="p-5 md:p-6 bg-gradient-to-r from-[#fde7f2]/40 to-[#fde2d3]/40">
                  <p className="text-sm font-semibold text-[#d03484] uppercase tracking-wider md:hidden mb-1">
                    recruteurs.io
                  </p>
                  <span className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base font-semibold text-[#30475e]">
                      {row.recruteurs}
                    </span>
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wider md:hidden mb-1">
                    Cabinet classique
                  </p>
                  <p className="text-base text-neutral-500">{row.classique}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — Garantie */}
      <SectionBackground page="local-marseille" section="lm-guarantee" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre garantie</span>

            <h2 className={h2Class}>
              Si ça ne tient pas dans les 6 mois,{' '}
              <span className={gradientText}>on recommence.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Une garantie chiffrée, opposable. Pour que l’engagement soit réel —
              pas un argument commercial.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guaranteeBullets.map((g, index) => {
              const Icon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{g.title}</h3>
                  <p className={bodyClass}>{g.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — Zones couvertes */}
      <SectionBackground page="local-marseille" section="lm-zones" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Zones couvertes</span>

            <h2 className={h2Class}>
              Marseille,{' '}
              <span className={gradientText}>Aix-en-Provence et toute la PACA.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Nous intervenons sur l’ensemble des bassins d’emploi de la région marseillaise
              et de la{' '}
              <a href="/cabinet-recrutement-paca" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
                PACA
              </a>{' '}
              selon la nature de la mission.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 max-w-4xl mx-auto mb-10">
            {zones.map((zone) => (
              <div
                key={zone}
                className="flex items-center gap-2 bg-[#fbfaf8] rounded-xl px-4 py-3 ring-1 ring-neutral-200"
              >
                <MapPin className="w-4 h-4 text-[#d03484] flex-shrink-0" />
                <span className="text-sm font-medium text-neutral-800">{zone}</span>
              </div>
            ))}
          </div>

          <div className="rounded-[2rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={bodyClass}>
              Pour des postes de direction, des profils rares ou des missions de chasse de
              tête, nous recrutons à l’échelle nationale tout en gardant une connaissance
              fine des contextes locaux. Si votre siège est à Marseille mais que le poste
              est basé à{' '}
              <a
                href="/cabinet-recrutement-aix-en-provence"
                className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
              >
                Aix-en-Provence
              </a>
              , nous intervenons sans contrainte de périmètre.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 11 — Logos clients (wall créatif partagé) */}
      <ClientLogosWall
        page="local-marseille"
        section="lm-trust"
        className="bg-[#fbfaf8]"
      />

      {/* SECTION 12 — Vu à la télé */}
      <SectionBackground page="local-marseille" section="lm-press" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>

            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé — vision du recrutement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 13 — FAQ enrichie */}
      <SectionBackground page="local-marseille" section="lm-faq" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Questions fréquentes</span>

            <h2 className={h2Class}>
              Sur le recrutement{' '}
              <span className={gradientText}>à Marseille.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Les questions que nos interlocuteurs posent le plus souvent avant de démarrer.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="/infos/questions"
              className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
            >
              Voir toutes les questions fréquentes →
            </a>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 14 — CTA final */}
      <SectionBackground page="local-marseille" section="lm-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Parlons de votre recrutement{' '}
              <span className={gradientText}>à Marseille.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Que vous ayez un poste précis à pourvoir ou un besoin plus large à cadrer, le
              point de départ est toujours le même : une conversation directe, sans
              engagement, pour voir si nous sommes le bon partenaire pour vous.
              <br /><br />
              Nous intervenons sur <strong>Marseille, Aix-en-Provence et l’ensemble de la PACA</strong>.
              Nos tarifs sont présentés lors de l’appel ou accessibles sur{' '}
              <a href="/infos/tarifs" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
                notre page tarifs
              </a>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Réserver un échange (15 min)
              </button>
              <button
                onClick={() => setIsHubSpotModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Nous écrire
              </button>
            </div>

            <p className="mt-8 text-sm text-neutral-500">
              Ou retrouvez-nous sur{' '}
              <a
                href="https://www.linkedin.com/company/recruteurs-io"
                className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              .
            </p>
          </div>
        </Section>
      </SectionBackground>

      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </Layout>
  );
}

// ─── FAQ Item ─────────────────────────────────────────────────────────

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-[#30475e] text-base leading-snug">
          {question}
        </span>
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-neutral-700 leading-relaxed border-t border-neutral-100 pt-4 text-base">
          {answer}
        </div>
      )}
    </div>
  );
}
